import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Plus, X, Trash2, Mail, Check, Ban } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";

const PRODUCTS = ["Registration fee", "Stamp Duty", "Clerical Fees", "Cleaner charges", "Plumber Charges", "Decorator Charges"];
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n || 0);
const BTN = "inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all active:scale-[0.98]";
const INPUT = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

export default function Invoices() {
  const { clients, selectedClientId } = usePortal();
  const client = clients.find((c) => c.id === selectedClientId);
  const [rate, setRate] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertyName, setPropertyName] = useState("");
  const [lines, setLines] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => api.get(`/admin/invoices${selectedClientId ? `?client_id=${selectedClientId}` : ""}`).then((r) => setHistory(r.data)).catch(() => {}), [selectedClientId]);
  useEffect(() => {
    api.get("/admin/fx/inr-gbp").then((r) => setRate(r.data.rate)).catch(() => {});
  }, []);

  const viewPdf = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/pdf`);
      if (!data.pdf) return toast.error("Invoice not available");
      const blob = await (await fetch(data.pdf)).blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open invoice"); }
  };

  useEffect(() => {
    setPropertyName(""); setLines([]);
    if (selectedClientId) {
      api.get(`/properties?client_id=${selectedClientId}`).then((r) => setProperties(r.data)).catch(() => setProperties([]));
      loadHistory();
    } else {
      setProperties([]); setHistory([]);
    }
  }, [selectedClientId, loadHistory]);

  const toggleProduct = (p) => setLines((ls) => (ls.find((l) => l.product === p) ? ls.filter((l) => l.product !== p) : [...ls, { product: p, amount_inr: "" }]));
  const setAmount = (p, v) => setLines((ls) => ls.map((l) => (l.product === p ? { ...l, amount_inr: v } : l)));
  const totalInr = lines.reduce((s, l) => s + (Number(l.amount_inr) || 0), 0);

  const generate = async () => {
    if (!propertyName) return toast.error("Select a property");
    if (lines.length === 0) return toast.error("Select at least one product");
    if (lines.some((l) => !(Number(l.amount_inr) > 0))) return toast.error("Enter an amount for each line");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        client_id: selectedClientId, property_name: propertyName,
        lines: lines.map((l) => ({ product: l.product, amount_inr: Number(l.amount_inr) })),
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  const cancelDraft = async () => {
    if (!preview) return;
    try { await api.delete(`/admin/invoices/${preview.id}`); } catch { /* ignore */ }
    setPreview(null);
    loadHistory();
    toast("Invoice cancelled");
  };

  const emailInvoice = async () => {
    if (!preview) return;
    try {
      await api.post(`/admin/invoices/${preview.id}/send`);
      toast.success(`Invoice emailed to ${preview.client_name}`);
      setPreview(null); setPropertyName(""); setLines([]);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not email invoice");
    }
  };

  const cancelSent = async (id) => {
    try { await api.delete(`/admin/invoices/${id}`); toast("Invoice cancelled"); loadHistory(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel"); }
  };

  return (
    <div className="max-w-5xl" data-testid="invoices-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2">
        <FileText className="h-7 w-7 text-emerald-600" /> Invoices
      </h1>
      <p className="mt-2 text-sm text-slate-500">Generate a branded invoice for the selected client, preview it, then email it with a secure pay link.</p>

      {!selectedClientId ? (
        <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-5 text-sm" data-testid="invoice-select-client-message">
          Please select a client from the <strong>Viewing client</strong> dropdown at the top-right first.
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">New invoice</h2>
            <p className="text-sm text-slate-500 mt-0.5">Client: <span className="font-medium text-slate-800" data-testid="invoice-client-name">{client?.name || "—"}</span></p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-sm text-slate-600">Property</label>
                <select value={propertyName} onChange={(e) => setPropertyName(e.target.value)} data-testid="invoice-property-select" className={`${INPUT} mt-1.5`}>
                  <option value="">{properties.length ? "Select a property…" : "No properties for this client"}</option>
                  {properties.map((p) => <option key={p.id} value={p.address}>{p.address}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Products</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PRODUCTS.map((p) => {
                    const on = !!lines.find((l) => l.product === p);
                    return (
                      <button key={p} type="button" onClick={() => toggleProduct(p)} data-testid={`invoice-product-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
                        {on ? <Check className="inline h-3 w-3 mr-1" /> : <Plus className="inline h-3 w-3 mr-1" />}{p}
                      </button>
                    );
                  })}
                </div>
              </div>
              {lines.length > 0 && (
                <div className="space-y-2" data-testid="invoice-lines">
                  {lines.map((l) => {
                    const g = rate ? (Number(l.amount_inr) || 0) * rate : 0;
                    return (
                      <div key={l.product} className="flex items-center gap-2" data-testid="invoice-line-row">
                        <span className="flex-1 text-sm text-slate-700">{l.product}</span>
                        <div className="relative w-32">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                          <input type="number" min="0" value={l.amount_inr} onChange={(e) => setAmount(l.product, e.target.value)} data-testid={`invoice-amount-${l.product.replace(/\s+/g, "-").toLowerCase()}`} className={`${INPUT} pl-6 py-2`} />
                        </div>
                        <span className="w-24 text-right text-xs text-slate-500 font-mono" data-testid="invoice-line-gbp">{gbp(g)}</span>
                        <button onClick={() => toggleProduct(l.product)} className="text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-sm font-semibold text-slate-900">
                    <span>Total</span>
                    <span className="font-mono" data-testid="invoice-total">{inr(totalInr)} · {gbp(rate ? totalInr * rate : 0)}</span>
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-400">{rate ? `Live rate: 1 INR = ${rate.toFixed(5)} GBP (ECB via Frankfurter). Payment is charged in GBP.` : "Fetching live exchange rate…"}</p>
              <button onClick={generate} disabled={generating} data-testid="generate-invoice-button" className={`${BTN} px-5 py-2.5 disabled:opacity-60`}>
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate invoice
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Recent invoices for this client</h2>
            <div className="mt-4 space-y-2 max-h-[560px] overflow-auto">
              {history.length === 0 && <p className="text-sm text-slate-400">No invoices sent to this client yet.</p>}
              {history.map((h) => (
                <div key={h.id} data-testid="invoice-history-row" className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 truncate">{h.property_name}</p>
                    <p className="text-xs text-slate-400">{(h.created_at || "").slice(0, 10)} · {gbp(h.total_gbp)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => viewPdf(h.id)} data-testid="invoice-view-pdf" className="text-slate-400 hover:text-emerald-700" title="View PDF"><FileText className="h-4 w-4" /></button>
                    {h.status === "sent" && (
                      <button onClick={() => cancelSent(h.id)} data-testid="invoice-cancel-sent" className="text-slate-400 hover:text-rose-600" title="Cancel invoice"><Ban className="h-4 w-4" /></button>
                    )}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${h.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : h.status === "sent" ? "bg-sky-50 text-sky-700 border-sky-200" : h.status === "cancelled" ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{h.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="w-full max-w-3xl h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="invoice-preview-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <p className="text-sm font-semibold text-slate-900">Invoice preview</p>
                <p className="text-xs text-slate-500">{preview.client_name} · {preview.property_name} · {gbp(preview.total_gbp)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={cancelDraft} data-testid="invoice-cancel-button" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white">
                  <Trash2 className="h-4 w-4" /> Cancel
                </button>
                <button onClick={emailInvoice} data-testid="invoice-email-button" className={`${BTN} px-4 py-2 text-sm`}>
                  <Mail className="h-4 w-4" /> Email to client
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-slate-100">
              <iframe title="invoice" src={`${preview.pdf}#toolbar=0`} className="w-full h-full border-0" data-testid="invoice-preview-frame" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
